/* eslint-disable */
import { fetch_form_tree } from '@/services/main_section_service';
import { resolve_workflow_display_stage } from '@/services/onboarding_journey_service';
import {
  create_application_status_approval_log,
  create_application_transition_log,
  fetch_assign_form_status_data,
  fetch_form_workflow_transition_rules,
  fetch_status_reviewers,
  fetch_user_roles
} from '@/services/workflow_service';
import Swal from 'sweetalert2';

export const WORKFLOW_ACTION_STATE = {
  workflow_actions_loading: false,
  workflow_actions_error: '',
  workflow_actions_by_kind: {},
  workflow_action_context: null,
  workflow_action_feedback: '',
  workflow_action_feedback_tone: 'success',
  workflow_action_in_progress: ''
};

function show_workflow_success(message) {
  Swal.fire({
    toast: true,
    position: 'top-end',
    icon: 'success',
    iconHtml: '&#10003;',
    title: String(message || ''),
    showConfirmButton: false,
    timer: 2600,
    timerProgressBar: false,
    showClass: {
      popup: 'swal2-toast-show'
    },
    hideClass: {
      popup: 'swal2-toast-hide'
    },
    customClass: {
      popup: 'success-toast-popup',
      title: 'success-toast-title',
      icon: 'success-toast-icon',
      iconHtml: 'success-toast-icon-check'
    }
  });
}

function show_workflow_error(message) {
  Swal.fire({
    toast: true,
    position: 'top-end',
    icon: 'error',
    iconHtml: '!',
    title: String(message || ''),
    showConfirmButton: false,
    timer: 3600,
    timerProgressBar: false,
    showClass: {
      popup: 'swal2-toast-show'
    },
    hideClass: {
      popup: 'swal2-toast-hide'
    },
    customClass: {
      popup: 'error-toast-popup',
      title: 'error-toast-title',
      icon: 'error-toast-icon',
      iconHtml: 'error-toast-icon-mark'
    }
  });
}

function show_workflow_warning(message) {
  Swal.fire({
    toast: true,
    position: 'top-end',
    icon: 'warning',
    iconHtml: '!',
    title: String(message || ''),
    showConfirmButton: false,
    timer: 3600,
    timerProgressBar: false,
    showClass: {
      popup: 'swal2-toast-show'
    },
    hideClass: {
      popup: 'swal2-toast-hide'
    },
    customClass: {
      popup: 'warning-toast-popup',
      title: 'warning-toast-title',
      htmlContainer: 'warning-toast-text',
      icon: 'warning-toast-icon',
      iconHtml: 'warning-toast-icon-mark'
    }
  });
}

function normalize_text_value(value) {
  return String(value || '').trim().toLowerCase();
}

function is_matching_status_name(left, right) {
  return normalize_text_value(left) === normalize_text_value(right);
}

function is_automatic_transition_action(action = {}) {
  return normalize_text_value(action?.transition_type) === 'automatic';
}

function actions_match_same_target(left = {}, right = {}) {
  return has_matching_target_id(left, right) || has_matching_target_name(left, right);
}

function has_matching_target_id(left = {}, right = {}) {
  return ids_match(left?.to_status_id, right?.to_status_id);
}

function has_matching_target_name(left = {}, right = {}) {
  const left_status_name = String(left?.to_status_name || '').trim();
  const right_status_name = String(right?.to_status_name || '').trim();
  return Boolean(left_status_name && right_status_name && is_matching_status_name(left_status_name, right_status_name));
}

function ids_match(left_id, right_id) {
  const left = Number(left_id || 0);
  const right = Number(right_id || 0);
  return left > 0 && right > 0 && left === right;
}

function normalize_assigned_form_status(status, index) {
  return {
    id: resolve_status_record_id(status),
    status_id: resolve_status_workflow_id(status),
    status_name: resolve_assigned_status_name(status, index),
    form_id: Number(status?.form_id || status?.assigned_form?.form_id || 0) || null,
    order: resolve_status_order(status, index),
    is_initial: Boolean(status?.is_initial),
    is_final: Boolean(status?.is_final)
  };
}

function resolve_status_record_id(status = {}) {
  return Number(status?.id || status?.assigned_form_status_id || 0);
}

function resolve_status_workflow_id(status = {}) {
  return Number(status?.status_id || status?.workflow_status_id || status?.id || 0);
}

function resolve_status_order(status = {}, index = 0) {
  return Number(status?.order || status?.order_number || index + 1);
}

function resolve_assigned_status_name(status, index) {
  return String(first_status_name_value(status) || `Status ${String(index + 1)}`).trim();
}

function first_status_name_value(status = {}) {
  return status?.status_name || status?.workflow_status_name || status?.status || status?.name;
}

function normalize_assigned_form_statuses(records = []) {
  return (Array.isArray(records) ? records : []).map(normalize_assigned_form_status);
}

function get_response_items(response) {
  return [
    response,
    response?.data,
    response?.results,
    response?.roles,
    response?.list_user_roles
  ].find(Array.isArray) || [];
}

function get_current_user_role_name(instance) {
  return instance?.$store?.getters?.currentRole
    || (typeof window !== 'undefined' ? window.sessionStorage.getItem('user_role') : '')
    || '';
}

function categorize_workflow_action(action = {}) {
  const action_name = normalize_text_value(action?.action);
  const to_status_name = normalize_text_value(action?.to_status_name);

  if (is_reject_action(action_name, to_status_name)) {
    return 'reject';
  }

  if (is_request_info_action(action_name, to_status_name)) {
    return 'request_info';
  }

  return 'advance';
}

function is_reject_action(action_name, to_status_name) {
  return ['reject', 'decline', 'fail'].some((word) => action_name.includes(word)) || to_status_name.includes('reject');
}

function is_request_info_action(action_name, to_status_name) {
  return ['request', 'change', 'info', 'remed'].some((word) => action_name.includes(word)) || ['in progress', 'submitted'].includes(to_status_name);
}

function get_application_form_id(form = {}) {
  return Number(form?.application_form_id || form?.id || 0);
}

function get_form_definition_id(form = {}) {
  return Number(form?.form_id || form?.form?.id || form?.application_form?.form_id || 0);
}

function has_application_form_id(form = {}) {
  return get_application_form_id(form) > 0;
}

function has_application_and_definition_ids(form = {}) {
  return has_application_form_id(form) && get_form_definition_id(form) > 0;
}

function get_primary_application_form(case_item = {}) {
  const detailed_forms = Array.isArray(case_item?.detailed_forms) ? case_item.detailed_forms : [];
  const forms = Array.isArray(case_item?.forms) ? case_item.forms : [];

  return find_primary_form_candidate(detailed_forms) || find_primary_form_candidate(forms) || null;
}

function find_primary_form_candidate(forms = []) {
  return forms.find(has_application_and_definition_ids) || forms.find(has_application_form_id) || null;
}

function status_matches_stage(status, current_stage) {
  return is_matching_status_name(status?.status_name, current_stage);
}

function resolve_current_assigned_status(case_item = {}) {
  const workflow_statuses = Array.isArray(case_item?.workflow_statuses) ? case_item.workflow_statuses : [];
  const current_stage = resolve_workflow_display_stage(case_item);
  return workflow_statuses.find((status) => status_matches_stage(status, current_stage)) || null;
}

function is_current_workflow_status_final(case_item = {}) {
  const current_status = resolve_current_assigned_status(case_item);
  return Boolean(current_status?.is_final);
}

function is_final_status(status = null) {
  return Boolean(status?.is_final);
}

function is_resolved_current_status_final(case_item = {}, current_status = null) {
  return is_final_status(current_status) || is_current_workflow_status_final(case_item);
}

function remove_advance_action_for_final_status(actions_by_kind = {}, is_final = false) {
  if (!is_final || !actions_by_kind.advance) return actions_by_kind;

  const filtered_actions = { ...actions_by_kind };
  delete filtered_actions.advance;
  return filtered_actions;
}

function resolve_current_assigned_form_status(assigned_form_statuses = [], case_item = {}) {
  const current_stage = resolve_workflow_display_stage(case_item);
  return (Array.isArray(assigned_form_statuses) ? assigned_form_statuses : []).find((status) => (
    status_matches_stage(status, current_stage)
  )) || null;
}

function status_matches_action_target(status = {}, action = {}) {
  const target_status_name = String(action?.to_status_name || '').trim();
  return (
    ids_match(status?.status_id, action?.to_status_id)
    || (target_status_name && is_matching_status_name(status?.status_name, target_status_name))
  );
}

function resolve_next_assigned_status(assigned_form_statuses = [], action = {}) {
  const workflow_statuses = Array.isArray(assigned_form_statuses) ? assigned_form_statuses : [];

  return workflow_statuses.find((status) => status_matches_action_target(status, action)) || null;
}

function resolve_next_status_by_order(assigned_form_statuses = [], current_status = null) {
  const current_order = Number(current_status?.order || 0);
  if (!current_order) return null;

  const next_statuses = assigned_form_statuses
    .filter((status) => Number(status?.order || 0) > current_order)
    .sort((left, right) => Number(left?.order || 0) - Number(right?.order || 0));
  return next_statuses[0] || null;
}

function build_fallback_advance_action(assigned_form_statuses = [], current_status = null) {
  const next_status = resolve_next_status_by_order(assigned_form_statuses, current_status);
  if (!next_status) return null;

  const action = {
    action: 'Advance',
    from_status_id: current_status?.status_id || null,
    from_status_name: current_status?.status_name || '',
    to_status_id: next_status?.status_id || null,
    to_status_name: next_status?.status_name || '',
    transition_type: 'manual',
    source: 'status_order_fallback'
  };
  return action;
}

function build_workflow_note(action = {}, next_status = null) {
  const action_label = String(action?.action || 'Advance').trim();
  const next_status_name = String(next_status?.status_name || action?.to_status_name || '').trim();
  if (!next_status_name) return `${action_label} transition`;
  return `${action_label} transition to ${next_status_name}`;
}

function action_requires_reason(action_kind = '') {
  return ['reject', 'request_info'].includes(String(action_kind || '').trim().toLowerCase());
}

function workflow_reason_prompt_config(action_kind = '', action = {}) {
  const action_name = String(action?.action || '').trim() || (
    String(action_kind || '').trim().toLowerCase() === 'request_info'
      ? 'Request information'
      : 'Reject'
  );
  if (String(action_kind || '').trim().toLowerCase() === 'request_info') {
    return {
      title: action_name,
      label: 'Reason for request',
      placeholder: 'Explain what must be corrected or added before this application can continue.',
      validation_message: 'A reason is required before requesting more information.'
    };
  }

  return {
    title: action_name,
    label: 'Reason for rejection',
    placeholder: 'Explain why this application is being rejected.',
    validation_message: 'A reason is required before rejecting this application.'
  };
}

async function prompt_workflow_reason(action_kind = '', action = {}) {
  if (!action_requires_reason(action_kind)) {
    return { confirmed: true, reason: '' };
  }

  const prompt = workflow_reason_prompt_config(action_kind, action);
  const result = await Swal.fire({
    title: prompt.title,
    input: 'textarea',
    inputLabel: prompt.label,
    inputPlaceholder: prompt.placeholder,
    inputAttributes: { 'aria-label': prompt.label },
    inputValidator: (value) => (
      String(value || '').trim() ? undefined : prompt.validation_message
    ),
    showCancelButton: true,
    confirmButtonText: 'Continue',
    cancelButtonText: 'Cancel',
    reverseButtons: true
  });

  return {
    confirmed: result.isConfirmed,
    reason: String(result.value || '').trim()
  };
}

function build_workflow_note_with_reason(action = {}, next_status = null, reason = '') {
  const normalized_reason = String(reason || '').trim();
  return normalized_reason || build_workflow_note(action, next_status);
}

function build_success_message(action = {}) {
  const action_name = String(action?.action || 'Workflow action').trim();
  return `${action_name} completed successfully.`;
}

function map_transition_rule_to_action(rule = {}) {
  const to_status = rule?.to_status || {};
  const from_status = rule?.from_status || {};

  return {
    action: get_transition_rule_action_name(rule),
    rule_id: rule?.id,
    from_status_id: get_transition_status_id(rule?.from_status_id, from_status),
    from_status_name: get_transition_status_name(from_status),
    to_status_id: get_transition_status_id(rule?.to_status_id, to_status),
    to_status_name: get_transition_status_name(to_status),
    transition_type_id: get_transition_type_id(rule),
    transition_type: get_transition_type_name(rule),
    min_approvals: Number(rule?.min_approvals || rule?.min_approvals_required || 1) || 1,
    condition: rule?.condition || {},
    source: 'transition_rule'
  };
}

function get_transition_rule_action_name(rule = {}) {
  return String(rule?.action || rule?.condition?.action || rule?.transition_action || 'Advance').trim();
}

function get_transition_status_id(raw_status_id, status = {}) {
  return Number(raw_status_id || status?.id || 0) || null;
}

function get_transition_status_name(status = {}) {
  return String(status?.status_name || status?.status || '').trim();
}

function get_transition_type_id(rule = {}) {
  return Number(rule?.transition_type_id || rule?.transition_type?.id || 0) || null;
}

function get_transition_type_name(rule = {}) {
  return String(rule?.transition_type?.name || rule?.transition_type || '').trim();
}

function merge_workflow_actions({
  transition_rule_actions = [],
  form_tree_actions = [],
  is_assigned_reviewer = false
}) {
  const normalized_transition_rule_actions = Array.isArray(transition_rule_actions) ? transition_rule_actions : [];
  const normalized_form_tree_actions = Array.isArray(form_tree_actions) ? form_tree_actions : [];

  const automatic_actions = get_automatic_workflow_actions(
    normalized_transition_rule_actions,
    normalized_form_tree_actions
  );
  const manual_actions = is_assigned_reviewer
    ? get_manual_workflow_actions(normalized_transition_rule_actions)
    : [];
  const fallback_manual_actions = is_assigned_reviewer
    ? get_manual_workflow_actions(normalized_form_tree_actions)
    : [];

  const merged_actions = [...automatic_actions, ...manual_actions];
  if (merged_actions.length) {
    return merged_actions;
  }

  if (automatic_actions.length) {
    return automatic_actions;
  }

  return fallback_manual_actions;
}

function get_automatic_workflow_actions(transition_rule_actions, form_tree_actions) {
  return transition_rule_actions.filter((action) => (
    is_automatic_transition_action(action)
    && form_tree_actions.some((allowed_action) => automatic_actions_match(action, allowed_action))
  ));
}

function automatic_actions_match(action, allowed_action) {
  return is_automatic_transition_action(allowed_action) && actions_match_same_target(action, allowed_action);
}

function get_manual_workflow_actions(actions = []) {
  return actions.filter((action) => !is_automatic_transition_action(action));
}

function is_user_assigned_to_status({ reviewers, current_user_id, current_role_id }) {
  const assigned_users = Array.isArray(reviewers?.users) ? reviewers.users : [];
  const assigned_roles = Array.isArray(reviewers?.roles) ? reviewers.roles : [];
  if (!assigned_users.length && !assigned_roles.length) return true;

  const has_user_assignment = assigned_users.some((item) => Number(item?.user_id || item?.id || 0) === Number(current_user_id || 0));
  const has_role_assignment = assigned_roles.some((item) => Number(item?.role_id || item?.id || 0) === Number(current_role_id || 0));
  return has_user_assignment || has_role_assignment;
}

export const workflowActionComputed = {
  current_user_id() {
    return Number(this.$store?.getters?.currentUserId || 0) || null;
  },
  current_user_role_name() {
    return get_current_user_role_name(this);
  },
  current_workflow_actions() {
    return this.workflow_actions_by_kind || {};
  },
  can_advance_stage() {
    const case_item = typeof this.get_workflow_action_case === 'function' ? this.get_workflow_action_case() : null;
    return Boolean(this.current_workflow_actions.advance) && !is_current_workflow_status_final(case_item);
  },
  can_request_info() {
    return Boolean(this.current_workflow_actions.request_info);
  },
  can_reject_stage() {
    return Boolean(this.current_workflow_actions.reject);
  }
};

export const workflowActionMethods = {
  reset_workflow_action_state() {
    this.workflow_actions_loading = false;
    this.workflow_actions_error = '';
    this.workflow_actions_by_kind = {};
    this.workflow_action_context = null;
    this.workflow_action_feedback = '';
    this.workflow_action_feedback_tone = 'success';
    this.workflow_action_in_progress = '';
  },
  async resolve_current_role_id() {
    const current_role_name = this.current_user_role_name;
    if (!current_role_name) return null;

    const response = await fetch_user_roles().catch(() => null);
    const roles = get_response_items(response);
    const matched_role = roles.find((role) => is_matching_status_name(
      role?.role || role?.name || role?.role_name,
      current_role_name
    ));
    return Number(matched_role?.id || matched_role?.role_id || 0) || null;
  },
  async load_workflow_actions_for_case(case_item = null) {
    const resolved_case = case_item || (typeof this.get_workflow_action_case === 'function' ? this.get_workflow_action_case() : null);
    const primary_form = get_primary_application_form(resolved_case);
    const application_id = Number(resolved_case?.application_id || 0);
    const application_form_id = Number(
      primary_form?.application_form_id
      || primary_form?.id
      || resolved_case?.application_form_id
      || resolved_case?.current_application_form_id
      || 0
    );
    const entity_profile_id = Number(
      resolved_case?.entity_profile_id
      || resolved_case?.profile?.id
      || primary_form?.entity_profile_id
      || 0
    ) || null;
    const current_assigned_status = resolve_current_assigned_status(resolved_case);
    const form_id = Number(
      primary_form?.form_id
      || primary_form?.form?.id
      || primary_form?.application_form?.form_id
      || primary_form?.application_form?.form?.id
      || primary_form?.application_form?.form_company_id
      || resolved_case?.form_id
      || resolved_case?.current_form_id
      || current_assigned_status?.form_id
      || 0
    ) || null;

    console.log('[WorkflowAction] Resolving case actions', {
      application_id,
      entity_profile_id,
      case_name: resolved_case?.name,
      current_stage: resolve_workflow_display_stage(resolved_case),
      current_workflow_status: resolved_case?.current_workflow_status,
      case_stage: resolved_case?.stage,
      forms: Array.isArray(resolved_case?.forms)
        ? resolved_case.forms.map((form) => ({
            id: form?.id,
            application_form_id: form?.application_form_id,
            form_id: form?.form_id,
            form_name: form?.form_name || form?.form?.name || form?.name
          }))
        : [],
      detailed_forms: Array.isArray(resolved_case?.detailed_forms)
        ? resolved_case.detailed_forms.map((form) => ({
            id: form?.id,
            application_form_id: form?.application_form_id,
            form_id: form?.form_id,
            form_name: form?.form_name || form?.form?.name || form?.name
          }))
        : [],
      workflow_statuses: Array.isArray(resolved_case?.workflow_statuses)
        ? resolved_case.workflow_statuses.map((status) => ({
            id: status?.id,
            status_id: status?.status_id,
            status_name: status?.status_name,
            is_initial: status?.is_initial,
            is_final: status?.is_final,
            transition_action: status?.transition_action,
            workflow_name: status?.workflow_name
          }))
        : [],
      selected_primary_form: primary_form
        ? {
            id: primary_form?.id,
            application_form_id: primary_form?.application_form_id,
            form_id: primary_form?.form_id,
            form_name: primary_form?.form_name || primary_form?.form?.name || primary_form?.name
          }
        : null,
      current_assigned_status: current_assigned_status
        ? {
            id: current_assigned_status?.id,
            status_id: current_assigned_status?.status_id,
            status_name: current_assigned_status?.status_name
          }
        : null
    });

    this.workflow_actions_loading = true;
    this.workflow_actions_error = '';
    this.workflow_actions_by_kind = {};
    this.workflow_action_context = null;

    if (!resolved_case || !application_id || !application_form_id || !form_id || !current_assigned_status) {
      console.warn('[WorkflowAction] Missing transition context', {
        has_case: Boolean(resolved_case),
        application_id,
        application_form_id,
        form_id,
        current_assigned_status
      });
      this.workflow_actions_error = 'No workflow transition is available for this case at the current stage yet.';
      this.workflow_actions_loading = false;
      return;
    }

    try {
      const [assigned_form_status_data, transition_rules_response, form_tree_response, reviewers, current_role_id] = await Promise.all([
        fetch_assign_form_status_data({
          form_id
        }).catch(() => ({ data: { assigned_statuses: [] } })),
        fetch_form_workflow_transition_rules({
          form_id,
          from_status_id: Number(current_assigned_status?.id || 0)
        }).catch(() => ({ data: [] })),
        fetch_form_tree({
          application_id,
          application_form_id,
          current_status_id: Number(current_assigned_status?.status_id || 0) || undefined,
          ...(entity_profile_id ? { entity_profile_id } : {})
        }).catch(() => ({ allowed_actions: [], user_id: null })),
        fetch_status_reviewers({
          form_id,
          status_id: Number(current_assigned_status?.status_id || 0)
        }).catch(() => ({ users: [], roles: [] })),
        this.resolve_current_role_id()
      ]);

      const assigned_form_statuses = normalize_assigned_form_statuses(
        assigned_form_status_data?.data?.assigned_statuses
        || assigned_form_status_data?.assigned_statuses
        || []
      );
      const workflow_status_fallbacks = normalize_assigned_form_statuses(resolved_case?.workflow_statuses || []);
      const available_statuses = assigned_form_statuses.length ? assigned_form_statuses : workflow_status_fallbacks;
      const current_assigned_form_status = resolve_current_assigned_form_status(
        available_statuses,
        resolved_case
      );

      const transition_rule_request = current_assigned_form_status?.id
        ? await fetch_form_workflow_transition_rules({
            form_id,
            from_status_id: Number(current_assigned_form_status.id || 0)
          }).catch(() => ({ data: [] }))
        : transition_rules_response;

      const transition_rules = Array.isArray(transition_rule_request?.data)
        ? transition_rule_request.data
        : (Array.isArray(transition_rule_request) ? transition_rule_request : []);
      const transition_rule_actions = transition_rules.map(map_transition_rule_to_action);
      const form_tree_actions = Array.isArray(form_tree_response?.allowed_actions) ? form_tree_response.allowed_actions : [];
      const is_assigned_reviewer = is_user_assigned_to_status({
        reviewers,
        current_user_id: this.current_user_id || form_tree_response?.user_id,
        current_role_id
      });
      const raw_allowed_actions = merge_workflow_actions({
        transition_rule_actions,
        form_tree_actions,
        is_assigned_reviewer
      });
      console.log('[WorkflowAction] Backend action context', {
        application_id,
        application_form_id,
        form_id,
        current_assigned_status_id: Number(current_assigned_status?.status_id || 0),
        current_assigned_form_status_id: Number(current_assigned_status?.id || 0),
        resolved_current_form_status_assignment_id: Number(current_assigned_form_status?.id || 0),
        assigned_form_statuses,
        transition_rules,
        reviewers,
        current_user_id: this.current_user_id || form_tree_response?.user_id,
        current_role_id,
        raw_allowed_actions,
        is_assigned_reviewer
      });
      const visible_actions = raw_allowed_actions.filter((action) => (
        !is_automatic_transition_action(action) || categorize_workflow_action(action) === 'advance'
      ));
      const actions_by_kind = visible_actions.reduce((accumulator, action) => {
        const kind = categorize_workflow_action(action);
        if (!accumulator[kind]) accumulator[kind] = action;
        return accumulator;
      }, {});
      const current_status_is_final = is_resolved_current_status_final(
        resolved_case,
        current_assigned_form_status || current_assigned_status
      );
      const fallback_advance_action = build_fallback_advance_action(
        available_statuses,
        current_assigned_form_status || current_assigned_status
      );
      if (!actions_by_kind.advance && fallback_advance_action && !current_status_is_final) {
        actions_by_kind.advance = fallback_advance_action;
      }

      this.workflow_actions_by_kind = remove_advance_action_for_final_status(
        actions_by_kind,
        current_status_is_final
      );
      this.workflow_action_context = {
        application_id,
        application_form_id,
        entity_profile_id,
        form_id,
        current_assigned_status,
        current_assigned_form_status,
        assigned_form_statuses: available_statuses,
        current_user_id: this.current_user_id || form_tree_response?.user_id || null,
        case_item: resolved_case
      };

      const has_manual_actions = raw_allowed_actions.some((action) => !is_automatic_transition_action(action));
      if (!is_assigned_reviewer && has_manual_actions) {
        this.workflow_actions_error = 'This stage is assigned to a different reviewer.';
      }
      console.log('[WorkflowAction] Visible actions', {
        application_id,
        visible_actions,
        workflow_actions_by_kind: this.workflow_actions_by_kind,
        workflow_actions_error: this.workflow_actions_error
      });
    } catch (error) {
      console.error('[WorkflowAction] Failed to resolve workflow actions', error);
      this.workflow_actions_error = error?.response?.data?.message || error?.message || 'Unable to resolve workflow actions for this case.';
    } finally {
      this.workflow_actions_loading = false;
    }
  },
  async run_selected_workflow_action(action_kind) {
    const action = this.current_workflow_actions?.[action_kind];
    const context = this.workflow_action_context;
    const case_item = context?.case_item || (typeof this.get_workflow_action_case === 'function' ? this.get_workflow_action_case() : null);
    console.log('[WorkflowAction] Attempting action', {
      action_kind,
      action,
      current_workflow_actions: this.current_workflow_actions,
      workflow_actions_error: this.workflow_actions_error,
      workflow_action_context: context
    });
    if (!action || !context || !case_item) {
      const message = this.workflow_actions_error || 'This workflow action is not available for the current case status.';
      console.warn('[WorkflowAction] Action unavailable', {
        action_kind,
        action,
        workflow_actions_error: this.workflow_actions_error,
        current_workflow_actions: this.current_workflow_actions,
        workflow_action_context: context
      });
      this.workflow_action_feedback = message;
      this.workflow_action_feedback_tone = 'warning';
      show_workflow_warning(message);
      return;
    }

    const current_status_is_final = is_resolved_current_status_final(
      case_item,
      context.current_assigned_form_status || context.current_assigned_status
    );
    if (action_kind === 'advance' && current_status_is_final) {
      const message = 'This workflow status is final and cannot be advanced.';
      this.workflow_action_feedback = message;
      this.workflow_action_feedback_tone = 'warning';
      show_workflow_warning(message);
      return;
    }

    const next_assigned_status = resolve_next_assigned_status(context?.assigned_form_statuses, action);
    if (!next_assigned_status) {
      this.workflow_action_feedback = 'The next workflow status could not be resolved for this case.';
      this.workflow_action_feedback_tone = 'warning';
      show_workflow_warning(this.workflow_action_feedback);
      return;
    }

    const workflow_reason = await prompt_workflow_reason(action_kind, action);
    if (!workflow_reason.confirmed) {
      return;
    }

    this.workflow_action_in_progress = action_kind;
    this.workflow_action_feedback = '';

    try {
      const approval_log = await create_application_status_approval_log({
        application_form_id: context.application_form_id,
        form_status_id: Number(
          context.current_assigned_form_status?.id
          || context.current_assigned_status?.id
          || 0
        ),
        actioned_by_id: context.current_user_id,
        action_taken: action?.action || 'Advance',
        is_system_triggered: false
      });

      const approval_log_id = Number(approval_log?.data?.id || approval_log?.id || 0);
      if (!approval_log_id) {
        throw new Error('The approval log could not be created for this transition.');
      }

      await create_application_transition_log({
        application_form_id: context.application_form_id,
        approval_log_id,
        from_status_id: Number(
          context.current_assigned_form_status?.id
          || context.current_assigned_status?.id
          || 0
        ),
        to_status_id: Number(next_assigned_status?.id || 0),
        note: build_workflow_note_with_reason(action, next_assigned_status, workflow_reason.reason),
        reviewer_id: context.current_user_id,
        is_system_triggered: false,
        entity_profile_id: context.entity_profile_id
      });

      this.workflow_action_feedback = build_success_message(action);
      this.workflow_action_feedback_tone = 'success';
      show_workflow_success(this.workflow_action_feedback);

      if (typeof this.on_workflow_action_success === 'function') {
        await this.on_workflow_action_success();
      }

      window.dispatchEvent(new CustomEvent('onboarding-data-updated'));
    } catch (error) {
      this.workflow_action_feedback = error?.response?.data?.message || error?.message || 'Unable to move this case to the next workflow stage.';
      this.workflow_action_feedback_tone = 'danger';
      show_workflow_error(this.workflow_action_feedback);
    } finally {
      this.workflow_action_in_progress = '';
    }
  }
};
